import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { formatDate, formatCost, formatDuration, calculateCostFromTokens } from '../../lib/utils';
import { CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';
import type { Job } from '../../types/api';

interface RecentJobsProps {
  jobs: Job[];
}

export function RecentJobs({ jobs }: RecentJobsProps) {
  const navigate = useNavigate();

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case 'failed':
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-red-400" />;
      case 'running':
      case 'pending':
        return <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-emerald-300 bg-emerald-950/50 border-emerald-800';
      case 'failed':
      case 'cancelled':
        return 'text-red-300 bg-red-950/50 border-red-800';
      case 'running':
      case 'pending':
        return 'text-cyan-300 bg-cyan-950/50 border-cyan-800';
      default:
        return 'text-slate-300 bg-slate-950/50 border-slate-800';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Jobs</CardTitle>
        <CardDescription>
          Your recent analysis jobs
        </CardDescription>
      </CardHeader>
      <CardContent>
        {jobs.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <p className="text-sm">No jobs yet.</p>
            <p className="text-xs mt-1">Run your first analysis to get started.</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
            {jobs.map((job) => (
              <div
                key={job.id}
                className="border rounded-lg p-3 space-y-2 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(job.status)}
                    <div className="text-sm">
                      <div className="font-medium">
                        {formatDate(job.created_at)}
                      </div>
                      <div className={`text-xs capitalize px-2 py-0.5 rounded-full inline-block border ${getStatusColor(job.status)}`}>
                        {job.status}
                      </div>
                    </div>
                  </div>
                </div>

                {job.status === 'completed' && (
                  <div className="text-xs text-muted-foreground space-y-1">
                    <div className="flex justify-between">
                      <span>Articles found:</span>
                      <span className="font-medium">{job.total_articles ?? 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Relevant (≥80):</span>
                      <span className="font-medium text-emerald-400">
                        {job.relevant_articles ?? 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Cost:</span>
                      <span className="font-medium">
                        {formatCost(job.estimated_cost ?? calculateCostFromTokens(job.input_tokens, job.output_tokens))}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Duration:</span>
                      <span className="font-medium">{formatDuration(job.duration_seconds)}</span>
                    </div>
                  </div>
                )}

                {job.status === 'failed' && job.error_message && (
                  <div className="text-xs text-red-600">
                    Error: {job.error_message}
                  </div>
                )}

                {job.status === 'running' && (
                  <div className="text-xs text-muted-foreground">
                    {job.current_step} - {job.progress}%
                  </div>
                )}

                {job.status === 'completed' && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate(`/results/${job.id}`)}
                  >
                    View Results
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
